import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OpenStreetMapComponent } from '@app/components/openstreetmap/openstreetmap.component';
import { MapControllerInput, MapInitOptions, Marker } from '@app/components/openstreetmap/interfaces';
import { RegionManagerService } from '@components/openstreetmap/managers/region-manager.service';
import { environment } from '@root/src/environments/environment';

declare var L: any;

export interface GalleryMapMarker {
  id: string;
  lat: number;
  lng: number;
  title?: string;
  popup?: string;
  isSelected?: boolean;
}

/** A polygon outline (e.g. a raster's footprint) drawn on top of the map; `coordinates` are `[lat, lng]` pairs. */
export interface GalleryMapFootprint {
  id: string;
  coordinates: [number, number][];
  title?: string;
  isSelected?: boolean;
}

export interface RegionBoundaryShape {
  id: string;
  level: number;
  label: string;
  coordinates: [number, number][];
}

export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

@Component({
  selector: 'app-gallery-map',
  standalone: true,
  templateUrl: './gallery-map.component.html',
  styleUrls: ['./gallery-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, OpenStreetMapComponent],
})
export class GalleryMapComponent implements OnChanges, OnDestroy {
  @ViewChild(OpenStreetMapComponent) osmComponent!: OpenStreetMapComponent;

  private readonly regionManager = inject(RegionManagerService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  @Input() markers: GalleryMapMarker[] = [];
  @Input() footprints: GalleryMapFootprint[] = [];
  @Input() center: [number, number] = [environment.map.lat, environment.map.lng];
  @Input() zoom: number = 9;
  @Input() selectedRegionBoundary: [number, number][] | null = null;

  @Input() level1Boundaries: RegionBoundaryShape[] = [];
  @Input() level2Boundaries: RegionBoundaryShape[] = [];
  @Input() level3Boundaries: RegionBoundaryShape[] = [];

  @Output() markerClick = new EventEmitter<{ id: string }>();
  @Output() footprintClick = new EventEmitter<{ id: string }>();
  @Output() boundsChange = new EventEmitter<MapBounds>();
  @Output() mapReady = new EventEmitter<void>();

  mapController = new Subject<MapControllerInput>();

  mapOptions: MapInitOptions = {
    center: [environment.map.lat, environment.map.lng],
    zoom: 9,
    height: '100%',
    showControls: true,
    minimalControls: false,
    drawingMode: false,
    showDrawingControls: false,
    baseMap: 'osm',
    showRegionNames: true,
    disableBounds: true,
  };

  private mapInstance: any = null;
  private boundaryLayerGroup: any = null;
  private footprintLayerGroup: any = null;
  private pendingFit: { bounds: [[number, number], [number, number]]; maxZoom: number; animate?: boolean } | null = null;
  private administrativeRegionsLoaded = false;

  private readonly REGION_LEVEL_SWITCH_ZOOM = 9;
  private readonly REGION_LEVEL2_MIN_ZOOM = 7;

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['markers']) {
      this.updateMarkers();
    }

    if (changes['footprints']) {
      this.updateFootprints();
    }

    if (changes['center'] || changes['zoom']) {
      if (this.center && this.center.length === 2) {
        this.mapController.next({
          setCenter: {
            lat: this.center[0],
            lng: this.center[1],
            zoom: this.zoom,
          },
        });

        setTimeout(() => this.emitBounds(), 1200);
      }
    }

    if (changes['level1Boundaries'] || changes['level2Boundaries'] || changes['level3Boundaries']) {
      if (this.mapInstance) {
        this.applyRegionBoundaryLayer();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.mapInstance && this.boundaryLayerGroup) {
      this.mapInstance.removeLayer(this.boundaryLayerGroup);
    }
  }

  onMapInstanceReady(mapInstance: any): void {
    if (!mapInstance) return;
    this.mapInstance = mapInstance;

    if (typeof L !== 'undefined') {
      this.boundaryLayerGroup = L.layerGroup().addTo(this.mapInstance);
      this.footprintLayerGroup = L.layerGroup().addTo(this.mapInstance);
    }

    this.mapInstance.setView(this.center, this.zoom);

    if (this.pendingFit) {
      this.mapInstance.fitBounds(this.pendingFit.bounds, {
        padding: [24, 24],
        maxZoom: this.pendingFit.maxZoom,
        animate: this.pendingFit.animate
      });
      this.pendingFit = null;
    }

    this.mapInstance.on('moveend', () => {
      this.onMapMove();
    });

    this.mapInstance.on('zoomend', () => {
      const currentZoom = this.mapInstance.getZoom();
      this.applyRegionBoundaryLayer(currentZoom);
      this.onMapMove();
    });

    this.updateMarkers();
    this.updateFootprints();
    this.loadAdministrativeRegionPolygonsOnce();
    this.applyRegionBoundaryLayer(this.zoom);

    setTimeout(() => {
      this.emitBounds();
      this.mapReady.emit();
    }, 200);
  }

  /** Full administrative region outlines (same path as other OSM pages after init refactor). */
  private loadAdministrativeRegionPolygonsOnce(): void {
    if (this.administrativeRegionsLoaded) {
      return;
    }
    this.administrativeRegionsLoaded = true;
    this.regionManager
      .toPolygon()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (polygons) => {
          if (polygons?.length) {
            this.regionManager.configurePolygonEvents(polygons, this.mapOptions, undefined);
            this.mapController.next({addGisPolygons: polygons});
            setTimeout(() => {
              this.mapController.next({
                setShowRegionNames: this.mapOptions.showRegionNames,
              });
            }, 100);
          }
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Gallery map: failed to load administrative region polygons', err);
          this.cdr.markForCheck();
        },
      });
  }

  private updateMarkers(): void {
    this.mapController.next({ clearMarkers: true });

    if (!this.markers || this.markers.length === 0) {
      return;
    }

    const osmMarkers: Marker[] = this.markers.map((m) => ({
      id: m.id,
      lat: m.lat,
      lng: m.lng,
      title: m.title,
      popup: m.popup,
      customIcon: m.isSelected ? this.getSelectedMarkerIcon() : undefined,
      onClick: () => this.onMarkerClick(m.id),
    }));

    setTimeout(() => {
      this.mapController.next({ addMarkers: osmMarkers });
    }, 50);
  }

  private updateFootprints(): void {
    if (!this.footprintLayerGroup || typeof L === 'undefined') {
      return;
    }

    this.footprintLayerGroup.clearLayers();

    (this.footprints ?? []).forEach((footprint) => {
      if (!footprint.coordinates || footprint.coordinates.length < 3) {
        return;
      }

      const polygon = L.polygon(footprint.coordinates, {
        color: footprint.isSelected ? '#ff5722' : '#2563eb',
        weight: footprint.isSelected ? 3 : 2,
        opacity: 0.9,
        fillColor: footprint.isSelected ? '#ff5722' : '#2563eb',
        fillOpacity: footprint.isSelected ? 0.2 : 0.1,
      });

      if (footprint.title) {
        // A DOM node, not a string: Leaflet treats a string tooltip as HTML, and a title is user-supplied.
        const label = document.createElement('div');
        label.textContent = footprint.title;
        polygon.bindTooltip(label, { sticky: true });
      }

      polygon.on('click', () => this.footprintClick.emit({ id: footprint.id }));
      this.footprintLayerGroup.addLayer(polygon);
    });
  }

  /**
   * Zooms the map so `bounds` (`[[south, west], [north, east]]`) is fully visible. `animate: false` (used for the
   * initial "show every result" fit) applies the view synchronously instead of relying on Leaflet's pan/zoom
   * animation, whose `requestAnimationFrame` steps a browser throttles or pauses for a backgrounded/hidden tab —
   * an animated fit issued right after a fast page load can otherwise never visibly complete, leaving the map (and
   * the viewport-bounded reload that follows it) stuck at its pre-fit view.
   */
  public fitBounds(bounds: [[number, number], [number, number]], maxZoom = 16, animate?: boolean): void {
    if (!this.mapInstance) {
      // The map is still initialising (it is created lazily); apply the request once it is ready instead of losing it.
      this.pendingFit = { bounds, maxZoom, animate };
      return;
    }
    this.mapInstance.fitBounds(bounds, { padding: [24, 24], maxZoom, animate });
  }

  /** Leaflet must be told when the container was resized or un-hidden, or it keeps drawing at the old size. */
  public invalidateSize(): void {
    this.mapInstance?.invalidateSize();
  }

  private onMarkerClick(id: string): void {
    this.markerClick.emit({ id });
  }

  private onMapMove(): void {
    this.emitBounds();
  }

  private emitBounds(): void {
    if (this.mapInstance) {
      const bounds = this.mapInstance.getBounds();
      if (bounds) {
        this.boundsChange.emit({
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        });
      }
    }
  }

  public applyRegionBoundaryLayer(currentZoom: number = this.zoom): void {
    if (!this.mapInstance || !this.boundaryLayerGroup || typeof L === 'undefined') {
      return;
    }

    let polygons: RegionBoundaryShape[] = [];

    if (currentZoom >= this.REGION_LEVEL_SWITCH_ZOOM) {
      polygons = this.level3Boundaries;
    } else if (currentZoom >= this.REGION_LEVEL2_MIN_ZOOM) {
      polygons = this.level2Boundaries;
    } else {
      polygons = this.level1Boundaries;
    }

    this.boundaryLayerGroup.clearLayers();

    if (!polygons || !polygons.length) {
      return;
    }

    polygons.forEach((boundary) => {
      if (!boundary.coordinates || boundary.coordinates.length === 0) return;

      const polygon = L.polygon(boundary.coordinates, {
        color: '#ef4444',
        weight: 2,
        opacity: 0.8,
        fillOpacity: 0,
        fillColor: 'transparent',
        className: 'region-boundary',
      });

      if (boundary.label) {
        polygon.bindTooltip(boundary.label, {
          permanent: false,
          direction: 'right',
          offset: [12, 0],
          className: 'region-boundary-tooltip',
          sticky: true,
        });
        this.setupTooltipEffects(polygon);
      }

      this.boundaryLayerGroup.addLayer(polygon);
    });
  }

  private setupTooltipEffects(polygon: any): void {
    let hoverTimeout: any;
    const hoverDelay = 2000;

    polygon.on('mouseover', (event: any) => {
      hoverTimeout = setTimeout(() => {
        polygon.openTooltip(event.latlng);
        const tooltip = polygon.getTooltip();
        const el = tooltip?.getElement();
        if (el) {
          el.style.opacity = '0';
          el.style.transition = 'opacity 0.25s ease';
          requestAnimationFrame(() => {
            el.style.opacity = '1';
          });
        }
      }, hoverDelay);
    });

    polygon.on('mousemove', (event: any) => {
      clearTimeout(hoverTimeout);
      const tooltip = polygon.getTooltip();
      if (tooltip?.isOpen()) {
        polygon.closeTooltip();
      }
      hoverTimeout = setTimeout(() => {
        polygon.openTooltip(event.latlng);
      }, hoverDelay);
    });

    polygon.on('mouseout', () => {
      clearTimeout(hoverTimeout);
      polygon.closeTooltip();
    });
  }

  private getSelectedMarkerIcon(): string {
    return `
      <div style="width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; transform: translate(0, -25%);">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3)); display: block;">
           <path fill="#2563eb" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" stroke="white" stroke-width="1.5"/>
        </svg>
      </div>
    `;
  }
}
